import { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { HelpOutline } from "@mui/icons-material";

type Example = { title: string; rows: [string, string, string][]; result: string };

// Examples follow the pooling in int_app_api/src/timesheets/giz-pooling.ts
const POOLED_EXAMPLES: Example[] = [
    {
        title: "Short days are added up",
        rows: [["Mon", "3 h", "-"], ["Tue", "3 h", "0.5"], ["Wed", "2 h", "0.5"]],
        result: "8 h = 1 day. Mon has no half day yet, its 3 h move on to Tue (and its description with them).",
    },
    {
        title: "Long day, the rest moves on",
        rows: [["5th", "10 h", "1"], ["6th", "2 h", "0.5"]],
        result: "12 h = 1.5 days. One calendar day gets at most 1 day, the 2 h left over count together with the next day.",
    },
    {
        title: "What is left at the end of the month",
        rows: [["Rest 2 h or more", "", "+ 0.5"], ["Rest under 2 h", "", "+ 0"]],
        result: "A rest under 2 h adds no time, only its description is added to the last billed day.",
    },
    {
        title: "Very little time in a month",
        rows: [["12th", "1 h", "0.5"]],
        result: "A month with any hours gets at least 0.5 day.",
    },
];

function ExampleTable({ example }: { example: Example }) {
    return (
        <Box sx={{ marginBottom: 2 }}>
            <Typography variant="subtitle2">{example.title}</Typography>
            <Table size="small" sx={{ maxWidth: 360, "& td, & th": { paddingY: 0.25 } }}>
                <TableHead>
                    <TableRow><TableCell>Day</TableCell><TableCell>Booked</TableCell><TableCell>Expert-days</TableCell></TableRow>
                </TableHead>
                <TableBody>
                    {example.rows.map(([day, hours, days]) => (
                        <TableRow key={day}><TableCell>{day}</TableCell><TableCell>{hours}</TableCell><TableCell>{days}</TableCell></TableRow>
                    ))}
                </TableBody>
            </Table>
            <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>{example.result}</Typography>
        </Box>
    );
}

// "?" button with a short tooltip; a click opens the full explanation with examples.
function GizAggregationHelp() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Tooltip title="How hours become 0.5 and 1 expert-days - click for examples">
                <IconButton size="small" onClick={() => setOpen(true)} aria-label="How the aggregation works"><HelpOutline fontSize="small" /></IconButton>
            </Tooltip>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>How hours become expert-days</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" paragraph>
                        GIZ bills in expert-days: <b>8 h = 1 day</b>, the smallest unit is <b>0.5 day = 4 h</b>.
                        Only hours booked as <i>Project implementation</i> count. You can always change the numbers in the review step before downloading.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Pool into half days (recommended)</Typography>
                    <Typography variant="body2" paragraph>
                        The hours of the month are added up day by day. Each time 4 h are together, that day gets 0.5 day.
                        Nothing is lost and nothing is rounded up per day: the month total is the booked hours / 8, rounded to the nearest 0.5.
                    </Typography>
                    {POOLED_EXAMPLES.map((example) => <ExampleTable key={example.title} example={example} />)}

                    <Typography variant="h6" gutterBottom>Per day</Typography>
                    <Typography variant="body2" paragraph>
                        Every day counts on its own: 8 h or more = 1 day, 4 h to under 8 h = 0.5 day, under 4 h = nothing.
                        Example: Mon 3 h, Tue 3 h, Wed 2 h gives <b>0 days</b> (pooled it would be 1 day).
                    </Typography>

                    <Typography variant="h6" gutterBottom>Aggregate across months</Typography>
                    <Typography variant="body2">
                        Only with several months and the pooled rule. Without it every month is closed on its own; with it the hours left over at the
                        end of a month move on to the next month, and the end-of-month rule applies only once, after the last month.
                        Example: July 5 h, August 5 h. Month by month: 0.5 + 0.5 = <b>1 day</b> (1 h is dropped each month).
                        Across months: July 0.5, August 1 = <b>1.5 days</b> (10 h / 8 = 1.25, rounded to 1.5).
                    </Typography>
                </DialogContent>
                <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
            </Dialog>
        </>
    );
}

export default GizAggregationHelp;
